import { AbstractControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { ipV4Pattern, ipV6Pattern, macAddressPattern } from '../regex-patterns';

export class CustomValidatorsFactory {
  public static createPhoneValidator(): ValidatorFn {
    // regex from: https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s03.html
    return (control: AbstractControl) => Validators.pattern(/^(\+|00|0)(?:[0-9] ?){6,14}[0-9]$/)(control)
      ? {
        phone: {
          error: true,
        },
      }
      : null;
  }

  public static createZipValidator(): ValidatorFn {
    return (control: AbstractControl) => Validators.pattern(/^[1-9]\d{3,6}$/)(control)
      ? {
        zip: {
          error: true,
        },
      }
      : null;
  }

  public static passwordValidation(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        // password should contain at least 6 digits, 1 upppercase, 1 lowercase, 1 number
        const pwdRule = new RegExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/g);
        const validPassword = pwdRule.test(control.value);
        return !validPassword ? {invalidPassword: true} : null;
      }
      return null;
    };
  }

  public static passwordMatchValidation(passwordControlName = 'password', passwordConfirmControlName = 'passwordConfirm'): ValidatorFn {
    return (group: UntypedFormGroup) => {
      const password = group.get(passwordControlName).value;
      const passwordConfirm = group.get(passwordConfirmControlName).value;
      if (!password || !passwordConfirm) {
        return null;
      }
      return password === passwordConfirm ? null : {passwordConfirmationMismatch: {valid: false}};
    };
  }

  public static forbiddenNameValidator(nameRe: RegExp): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const forbidden = nameRe.test(control.value);
      return forbidden ? {forbiddenName: {value: control.value}} : null;
    };
  }

  public static accessCodeDateValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        const date = DateTime.fromJSDate(control.value);
        const valid = date.isValid;
        if (valid) {
          if (date < DateTime.now().startOf('day')) {
            return {invalidAccessCodeDateMin: true};
          }
          if (date > DateTime.now().plus({years: 1})) {
            return {invalidAccessCodeDateMax: true};
          }
        }
        return !valid ? {invalidDate: true} : null;
      } else {
        return null;
      }
    };
  }

  public static atLeastOneFromFormGroupRequired(): ValidatorFn {
    return (group: UntypedFormGroup) => {
      const isTrue = (element) => !!group.get(element).value;
      const valid = Object.keys(group.controls).some(isTrue);
      return valid ? null : {error: true};
    };
  }


  public static validateIpAddress(allowIPv6: boolean = true): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.dirty && control.value) {
        let regexp: RegExp;
        if (allowIPv6) {
          regexp = new RegExp(`(${ipV6Pattern})|(${ipV4Pattern})`, 'i');
          return regexp.test(control.value) ? null : {ipV6Address: true};
        } else {
          regexp = new RegExp(`${ipV4Pattern}`, 'i');
          return regexp.test(control.value) ? null : {ipAddress: true};
        }
      }
      return null;
    };
  }

  public static validateMacAddress(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.dirty && control.value) {
        let regexp: RegExp;
        regexp = new RegExp(`${macAddressPattern}`, 'i');
        return regexp.test(control.value) ? null : {macAddress: true};
      }
      return null;
    };
  }

  public static validateDateNotInPast(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.dirty && control.value) {
        const date = DateTime.fromJSDate(control.value);
        const valid = date.isValid;
        if (valid) {
          if (date < DateTime.now().startOf('day')) {
            return {invalidDateInPast: true};
          }
        }
        return !valid ? {invalidDate: true} : null;
      } else {
        return null;
      }
    };
  }

  public static validateDateNotInFuture(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.dirty && control.value) {
        const date = DateTime.fromJSDate(control.value);
        const valid = date.isValid;
        if (valid) {
          if (date > DateTime.now().startOf('day')) {
            return {invalidDateInFuture: true};
          }
        }
        return !valid ? {invalidDate: true} : null;
      } else {
        return null;
      }
    };
  }

  public static validateDateNotBeforeDate(dateToCompare: AbstractControl): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.dirty && control.value) {
        const date = DateTime.fromJSDate(control.value);
        const valid = date.isValid;
        if (valid) {
          if (dateToCompare?.value != null && date < DateTime.fromJSDate(dateToCompare.value)) {
            return {invalidDateBeforeDate: true};
          }
        }
        return !valid ? {invalidDate: true} : null;
      } else {
        return null;
      }
    };
  }

  public static controlPlateValidatorFn(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      if (!value) return null;

      // Only uppercase A-Z, umlauts, and digits
      const regex = /^[A-ZÄÖÜ0-9]+$/;
      return regex.test(value) ? null : { controlPlateInvalid: true };
    };
  }
}
